import { DASHBOARD_CONFIG, type DashboardSettings, type DashboardTile, defaultDashboardConfig } from "@pentacode/core";
import "@pentacode/ui/components";
import { css, html, LitElement, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Checkbox } from "../../elements/checkbox";
import "../../elements/date-input";
import "../../elements/popover";
import "../../elements/scroller";
import "../../elements/sortable-list";
import { app } from "../../init";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";

import { provide } from "@lit/context";
import { enabledFeatures, type EnabledFeatures, loadEnabledFeatures } from "../../contexts/features-context";
import "./components/dashboard-tile/base-tile";
import "./components/dashboard-tile/tile-birthdays-anniversaries";
import "./components/dashboard-tile/tile-contract-deadlines";
import "./components/dashboard-tile/tile-notes";
import "./components/dashboard-tile/tile-recruiting";
import "./components/dashboard-tile/tile-savings-report";
import "./components/dashboard-tile/tile-shifts";
import "./components/dashboard-tile/tile-sick-leave";
import "./components/dashboard-tile/tile-tasks";
import "./components/dashboard-tile/tile-time-log-comments";
import "./components/dashboard-tile/tile-vacation";
import { mergeDashboardSettings } from "./utils/merge-settings";

type TileItem = {
    tile: DashboardTile;
    order: number;
};

export function hasDashboardTilePermission(tile: DashboardTile) {
    const requiredPermission = DASHBOARD_CONFIG.tiles[tile].permissions;
    if (requiredPermission.length === 0) {
        return true;
    }
    return requiredPermission.every((permission) => app.hasPermission(permission));
}

@customElement("ptc-dashboard")
export class Dashboard extends Routing(StateMixin(LitElement)) {
    routePattern = /^dashboard/;

    get routeTitle() {
        return "Dashboard";
    }

    get helpPage() {
        return "/handbuch/dashboard";
    }

    @provide({ context: enabledFeatures })
    @state()
    private _features: EnabledFeatures = loadEnabledFeatures();

    @state()
    private _showBetaTile: boolean = false;

    @state()
    private _availableTiles: Array<DashboardTile> = [];

    @state()
    private _permittedTiles: Array<DashboardTile> = [];

    @state()
    private _errorMessage: string = "";

    // Computed property to reduce repeated settings access
    private get _dashboardTiles(): DashboardSettings["tiles"] {
        return app.settings.dashboard.tiles;
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            .dashboard {
                position: relative;
                display: grid;
                min-height: 100%;
                grid-template-columns: auto;
                grid-template-rows: min-content auto;
                grid-template-areas:
                    "controls"
                    "tiles";
            }

            .tile-controls {
                position: absolute;
                place-self: start end;
                margin: 1rem;
                grid-area: controls;
            }

            .tile-controls form {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                justify-content: start;
            }

            .tile-controls form label {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                padding: 0;
            }

            .tile-item {
                place-content: stretch;
                display: grid;
                grid-template-columns: min-content auto;
                gap: 0;

                &:hover {
                    .grab-handle {
                        opacity: 1;
                    }
                }
            }

            .grab-handle {
                cursor: grab;
                opacity: 0.5;
                place-self: center;
                transition: opacity 0.2s ease-in-out;
            }

            .dashboard-tiles {
                --tile-base-size: 15rem;
                --tile-gap: 1rem;

                z-index: 2;
                padding: 1rem;
                gap: var(--tile-gap);
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(var(--tile-base-size), 1fr));
                grid-auto-rows: min-content;
                grid-auto-flow: dense;
                grid-area: tiles;
            }

            .dashboard-header {
                display: flex;
                justify-content: space-between;
                gap: 0.5rem;
                padding: 1.5rem 1rem 0;
            }

            .dashboard-header h1 {
                font-size: 1.5rem;
                font-weight: 600;
                margin: 0;
                background: var(--gradient-brand);
                -webkit-text-fill-color: transparent;
                background-clip: text;
            }

            .dashboard-tiles.filler {
                overflow: hidden;
                position: absolute;
                inset: 0;
                pointer-events: none;
                z-index: 1;

                &::after {
                    content: "";
                    position: absolute;
                    inset: 0;
                    top: auto;
                    height: 15rem;
                    background: linear-gradient(to bottom, transparent, var(--color-bg-light));
                }
            }

            .beta-tile {
                padding: 1rem;
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                background: var(--gradient-purple);
                color: var(--color-bg);
                height: var(--tile-base-size);
            }

            .beta-tile h2 {
                font-size: 1.2rem;
                font-weight: 600;
                margin: 0;
            }

            .tile-controls-header {
                display: flex;
                flex-direction: row;
                justify-content: center;
            }
        `,
    ];

    async connectedCallback() {
        super.connectedCallback();

        if (app.company) {
            this._features = loadEnabledFeatures(app.company?.isBetaCustomer);
            await app.updateSettings({
                dashboard: mergeDashboardSettings(defaultDashboardConfig, app.settings.dashboard),
            });
        }
    }

    private _handleError(error: unknown, operation: string) {
        console.error(`Dashboard ${operation} failed:`, error);
        this._errorMessage = `Einstellungen konnten nicht gespeichert werden. Bitte versuchen Sie es erneut.`;
        this.requestUpdate();

        // Clear error message after 5 seconds
        setTimeout(() => {
            this._errorMessage = "";
            this.requestUpdate();
        }, 5000);
    }

    /**
     * disable all tiles that are accessible
     * this makes sure that no other that that could be stored locally are shown
     */
    private _hideAllPermittedTiles() {
        const enabledTiles = Object.fromEntries(
            this._permittedTiles.map((tile) => [tile, { enabled: false }])
        ) as DashboardSettings["tiles"];

        app.updateSettings({
            dashboard: mergeDashboardSettings(app.settings.dashboard, {
                tiles: enabledTiles,
            }),
        })
            .catch((error) => this._handleError(error, "hide tiles"))
            .finally(() => {
                this.requestUpdate();
            });
    }

    /**
     * enable all tiles that are accessible
     * this makes sure that all tiles that are accessible are shown
     */
    private _showAllPermittedTiles() {
        const enabledTiles = Object.fromEntries(
            this._permittedTiles.map((tile) => [tile, { enabled: true }])
        ) as DashboardSettings["tiles"];

        app.updateSettings({
            dashboard: mergeDashboardSettings(app.settings.dashboard, {
                tiles: enabledTiles,
            }),
        })
            .catch((error) => this._handleError(error, "show tiles"))
            .finally(() => {
                this.requestUpdate();
            });
    }

    private _updateShownTiles(ev: InputEvent) {
        const target = ev.target as HTMLInputElement;
        if (!target || target.type !== "checkbox") return;

        const tileKey = target.value as DashboardTile;
        const enabled = target.checked;

        // Runtime validation: ensure tileKey is a valid dashboard tile
        if (!tileKey || !(tileKey in DASHBOARD_CONFIG.tiles)) return;

        app.updateSettings({
            dashboard: mergeDashboardSettings(app.settings.dashboard, {
                tiles: {
                    [tileKey]: {
                        enabled,
                    },
                } as DashboardSettings["tiles"],
            }),
        })
            .catch((error) => this._handleError(error, "update tile"))
            .finally(() => {
                this.requestUpdate();
            });
    }

    private _updateTileOrder(ev: CustomEvent) {
        const { fromIndex, toIndex } = ev.detail;

        // Create ordered list of tiles based on their current order
        const sortedTiles = this._availableTiles
            .map((tile) => ({
                tile,
                order: this._dashboardTiles[tile as DashboardTile]?.order ?? 0,
            }))
            .sort((a, b) => a.order - b.order);

        // Move the tile from fromIndex to toIndex
        const [movedTile] = sortedTiles.splice(fromIndex, 1);
        sortedTiles.splice(toIndex, 0, movedTile);

        // Update order values based on new positions
        const updatedTiles = Object.fromEntries(
            sortedTiles.map((item, index) => [
                item.tile,
                {
                    ...this._dashboardTiles[item.tile as DashboardTile],
                    order: index + 1,
                },
            ])
        ) as DashboardSettings["tiles"];

        app.updateSettings({
            dashboard: mergeDashboardSettings(app.settings.dashboard, {
                tiles: updatedTiles,
            }),
        })
            .catch((error) => this._handleError(error, "update tile order"))
            .finally(() => {
                this.requestUpdate();
            });
    }

    private _getAvailableTiles() {
        return (Object.keys(DASHBOARD_CONFIG.tiles) as DashboardTile[]).filter((key) => {
            const tileConfig = DASHBOARD_CONFIG.tiles[key];
            // check here if the tile is behind a feature flag
            if ("featureFlag" in tileConfig && tileConfig.featureFlag) {
                // Only show if the feature is enabled
                return Boolean(this._features?.[tileConfig.featureFlag as keyof EnabledFeatures]);
            }
            return true;
        });
    }

    private _getPermittedTiles() {
        return this._availableTiles.filter((tile) => hasDashboardTilePermission(tile));
    }

    /**
     * ensure the tiles reflect the available tiles
     */
    private async _cleanUpTiles() {
        const cleanedupTiles = Object.fromEntries(
            Object.entries(this._dashboardTiles).filter(([tile]) =>
                this._permittedTiles.includes(tile as DashboardTile)
            )
        ) as DashboardSettings["tiles"];

        return await app
            .updateSettings({
                dashboard: mergeDashboardSettings(app.settings.dashboard, {
                    tiles: cleanedupTiles,
                }),
            })
            .catch((error) => this._handleError(error, "cleanup tiles"));
    }

    render() {
        // We need to do this during render as the company is not available on every rerender
        if (app.company) {
            this._availableTiles = this._getAvailableTiles();
            this._permittedTiles = this._getPermittedTiles();

            this._cleanUpTiles().catch((error) => this._handleError(error, "cleanup tiles"));
        }

        return html`<div class="dashboard">
            ${this._errorMessage ? html`<div class="error-message">${this._errorMessage}</div>` : nothing}
            <header class="dashboard-header">
                <h1>Hallo ${app.account?.firstName || "Benutzer"} – Willkommen im Pentacode Cockpit!</h1>
                <button class="slim transparent layout" title="Dashboard Einstellungen">
                    <ptc-ui-icon name="icon.toggle.select-all" class="large"></ptc-ui-icon>
                </button>
                <ptc-popover trigger="click">
                    <form @change=${this._updateShownTiles}>
                        <fieldset class="tile-controls-header">
                            <button type="button" @click=${this._hideAllPermittedTiles} class="transparent slim">
                                <ptc-ui-icon name="icon.toggle.select-none"></ptc-ui-icon> Keine
                            </button>
                            <button type="button" @click=${this._showAllPermittedTiles} class="transparent slim">
                                <ptc-ui-icon name="icon.toggle.select-all"></ptc-ui-icon> Alle
                            </button>
                        </fieldset>
                        <ptc-sortable-list
                            .items=${this._permittedTiles
                                .map((tile) => ({
                                    tile,
                                    order: this._dashboardTiles[tile as DashboardTile]?.order ?? 0,
                                }))
                                .sort((a, b) => a.order - b.order)}
                            .renderItem=${(item: TileItem) => html`
                                <div class="tile-item">
                                    <ptc-ui-icon name="icon.action.reorder" class="grab-handle"></ptc-ui-icon>
                                    <ptc-checkbox-button
                                        name="tiles"
                                        buttonClass="transparent slim"
                                        .label=${DASHBOARD_CONFIG.tiles[item.tile]?.label || item.tile}
                                        .value=${item.tile}
                                        ?checked=${this._dashboardTiles[item.tile].enabled}
                                    ></ptc-checkbox-button>
                                </div>
                            `}
                            @item-moved=${this._updateTileOrder}
                        ></ptc-sortable-list>
                    </form>
                </ptc-popover>
            </header>
            <div class="dashboard-tiles">
                ${Object.entries(this._dashboardTiles)
                    .filter(
                        ([tileKey, config]) => config.enabled && this._permittedTiles.includes(tileKey as DashboardTile)
                    )
                    .sort(([, a], [, b]) => a.order - b.order)
                    .map(([tileKey]) => {
                        switch (tileKey as DashboardTile) {
                            case "birthdays":
                                return html`<ptc-dashboard-tile-birthdays-anniversaries
                                    .size=${this._dashboardTiles.birthdays.size}
                                ></ptc-dashboard-tile-birthdays-anniversaries>`;
                            case "sickLeaveRequests":
                                return html`<ptc-dashboard-tile-sick-leave
                                    .size=${this._dashboardTiles.sickLeaveRequests.size}
                                ></ptc-dashboard-tile-sick-leave>`;
                            case "vacationRequests":
                                return html`<ptc-dashboard-tile-vacation
                                    .size=${this._dashboardTiles.vacationRequests.size}
                                ></ptc-dashboard-tile-vacation>`;
                            case "contractDeadlines":
                                return html`<ptc-dashboard-tile-contract-deadlines
                                    .size=${this._dashboardTiles.contractDeadlines.size}
                                ></ptc-dashboard-tile-contract-deadlines>`;
                            case "notes":
                                return html`<ptc-dashboard-tile-notes
                                    .size=${this._dashboardTiles.notes.size}
                                ></ptc-dashboard-tile-notes>`;
                            case "shifts":
                                return html`<ptc-dashboard-tile-shifts
                                    .size=${this._dashboardTiles.shifts.size}
                                ></ptc-dashboard-tile-shifts>`;
                            case "savingsReport":
                                return html`<ptc-dashboard-tile-savings-report
                                    .size=${this._dashboardTiles.savingsReport.size}
                                ></ptc-dashboard-tile-savings-report>`;
                            case "recruitingApplications":
                                return html`<ptc-dashboard-tile-recruiting
                                    .size=${this._dashboardTiles.recruitingApplications.size}
                                ></ptc-dashboard-tile-recruiting>`;
                            case "tasks":
                                return html`<ptc-dashboard-tile-tasks
                                    .size=${this._dashboardTiles.tasks.size}
                                ></ptc-dashboard-tile-tasks>`;
                            case "timeLogComments":
                                return html`<ptc-dashboard-tile-time-log-comments
                                    .size=${this._dashboardTiles.timeLogComments.size}
                                ></ptc-dashboard-tile-time-log-comments>`;
                            default:
                                return nothing;
                        }
                    })}
                ${this._showBetaTile
                    ? html`<ptc-dashboard-tile .size=${1}>
                          <div class="beta-tile">
                              <h2>Danke für deine Teilnahme am Pilot - Programm!</h2>
                              <p>Wir freuen uns über deine Rückmeldung und Vorschläge!</p>
                          </div>
                      </ptc-dashboard-tile> `
                    : nothing}
            </div>
            <div class="dashboard-tiles filler">
                ${Object.entries(this._dashboardTiles)
                    .sort(([, a], [, b]) => a.order - b.order)
                    .map(
                        ([, config]) => html`
                            <ptc-dashboard-tile .size=${config.size} ?hidden=${!config.enabled}></ptc-dashboard-tile>
                        `
                    )}
                <ptc-dashboard-tile .size=${1}
                    ><span class="text-shade-5 place-self-center">mehr folgt bald…</span></ptc-dashboard-tile
                >
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${2}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
            </div>
        </div> `;
    }
}
